import { TRANSFORMERS } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import clsx from 'clsx';
import type { EditorState, SerializedEditorState } from 'lexical';
import { type FC, useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import FloatingFormatToolbar from '@/components/editor/components/floating-format-toolbar';
import { HighlightDifferencePlugin } from '@/components/editor/components/highlight-difference-plugin';
import { MarkdownFormattedViewPlugin } from '@/components/editor/components/markdown-formatted-view-plugin';
import MentionsPlugin from '@/components/editor/components/mention/mentions-plugin';
import styles from '@/components/editor/editor.module.css';
import useFlowStore from '@/store/flow';
import type { ChangeSet } from '@/types/common.ts';
import {
	editorValueToMarkdown,
	editorValueToPlainText,
	normalizeEditorValue,
	normalizePlainMarkdownEditorState,
	plainTextToEditorState
} from '@/utils/editor-conversion';

type EditorProps = {
	isDisabled: boolean;
	value: SerializedEditorState | string;
	onChange: (value: SerializedEditorState) => void;
	isTitle?: boolean;
	isRemoveActive?: boolean;
	isAddedActive?: boolean;
	changeSet: ChangeSet;
};

const customTransformers = [...TRANSFORMERS];

const Editor: FC<EditorProps> = ({
	onChange,
	isDisabled,
	value,
	isTitle,
	isRemoveActive,
	isAddedActive,
	changeSet
}) => {
	const [editor] = useLexicalComposerContext();
	const normalizedValue = useMemo(() => normalizeEditorValue(value), [value]);
	const currentMarkdownValue = useMemo(
		() => editorValueToMarkdown(value),
		[value]
	);
	const currentPlainTextValue = useMemo(
		() => editorValueToPlainText(value),
		[value]
	);

	const { cardToCompare, mentions, isComparingMode, isFormattedView } =
		useFlowStore(
			useShallow((state) => ({
				mentions: state.mentionOptions,
				isComparingMode: state.flowDrawer.isComparingMode,
				cardToCompare: state.flowDrawer.cardToCompare,
				isFormattedView: state.flowDrawer.isFormattedView
			}))
		);

	const syncedEditorValue = useMemo(
		() =>
			isFormattedView
				? normalizedValue
				: plainTextToEditorState(currentMarkdownValue),
		[currentMarkdownValue, isFormattedView, normalizedValue]
	);

	const skipOnChange = useRef(false);
	const lastSyncedValueRef = useRef<string | null>(null);

	const handleChange = (editorState: EditorState) => {
		if (!editor.isEditable()) {
			return;
		}
		if (skipOnChange.current) {
			skipOnChange.current = false;
			return;
		}

		const serializedState = editorState.toJSON();

		const nextState = isFormattedView
			? serializedState
			: normalizePlainMarkdownEditorState(serializedState);

		const serializedNextState = JSON.stringify(nextState);

		if (serializedNextState === lastSyncedValueRef.current) {
			return;
		}

		lastSyncedValueRef.current = serializedNextState;
		onChange(nextState);
	};

	useEffect(() => {
		editor.setEditable(!isDisabled);
	}, [isDisabled, editor]);

	useEffect(() => {
		const serializedValue = JSON.stringify(syncedEditorValue);
		if (serializedValue === lastSyncedValueRef.current) {
			return;
		}

		try {
			skipOnChange.current = true;
			editor.setEditorState(editor.parseEditorState(serializedValue));
			lastSyncedValueRef.current = serializedValue;
		} catch {
			skipOnChange.current = false;
		}
	}, [editor, syncedEditorValue]);

	return (
		<div className={styles.wrapper}>
			<RichTextPlugin
				contentEditable={
					<ContentEditable
						className={clsx('notranslate', styles.editable, {
							[styles.disabled]: isDisabled,
							[styles.titleEditor]: isTitle,
							[styles.empty]: !currentPlainTextValue
						})}
						translate="no"
					/>
				}
				ErrorBoundary={LexicalErrorBoundary}
				placeholder={
					isRemoveActive ? (
						<div className={styles.placeholder}>Enter some text...</div>
					) : null
				}
			/>
			<MarkdownShortcutPlugin transformers={customTransformers} />
			<HistoryPlugin />
			<OnChangePlugin onChange={handleChange} />
			<FloatingFormatToolbar />
			<MentionsPlugin
				options={mentions}
				trigger="@"
			/>
			<MarkdownFormattedViewPlugin skipOnChange={skipOnChange} />

			{isComparingMode && cardToCompare?.idn ? (
				<HighlightDifferencePlugin
					changeSet={changeSet}
					isAddedActive={isAddedActive}
					isRemoveActive={isRemoveActive}
					skipOnChange={skipOnChange}
				/>
			) : undefined}
		</div>
	);
};

export default Editor;
